import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Palette, 
  Image, 
  Type, 
  Mail, 
  Shield,
  Save,
  Info,
  Lock,
  Upload,
  X,
  Check,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

// Asset configuration matching backend validation
const ASSET_CONFIGS = {
  logo: {
    label: "App Logo",
    description: "Used in navigation header",
    constraints: "PNG or SVG, 256×256 to 512×512px",
    accept: ".png,.svg",
    minWidth: 256,
    minHeight: 256,
    maxWidth: 1024,
    maxHeight: 1024
  },
  symbol: {
    label: "Symbol",
    description: "Shown in footer (left of © text)",
    constraints: "PNG or SVG, 64×64 to 128×128px, transparent background required",
    accept: ".png,.svg",
    minWidth: 64,
    minHeight: 64,
    maxWidth: 256,
    maxHeight: 256
  },
  favicon: {
    label: "Favicon",
    description: "Browser tab icon",
    constraints: "PNG, SVG, or ICO, 16×16 to 48×48px (32×32 preferred)",
    accept: ".png,.svg,.ico",
    minWidth: 16,
    minHeight: 16,
    maxWidth: 64,
    maxHeight: 64
  }
};

/**
 * Asset Upload Component
 * Handles file selection, validation, preview, and upload
 */
function AssetUploader({ 
  assetType, 
  currentUrl, 
  onUploadSuccess, 
  onDelete 
}) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);
  
  const config = ASSET_CONFIGS[assetType];
  
  const validateFile = async (file) => {
    // Check file type
    const ext = file.name.split('.').pop().toLowerCase();
    const validExts = config.accept.split(',').map(e => e.replace('.', ''));
    
    if (!validExts.includes(ext)) {
      return `Invalid file type. Allowed: ${validExts.join(', ').toUpperCase()}`;
    }
    
    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      return "File too large. Maximum size: 5MB";
    }
    
    // For non-SVG, validate dimensions
    if (ext !== 'svg' && ext !== 'ico') {
      return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => {
          const { width, height } = img;
          if (width < config.minWidth || height < config.minHeight) {
            resolve(`Image too small. Minimum: ${config.minWidth}×${config.minHeight}px`);
          } else if (width > config.maxWidth || height > config.maxHeight) {
            resolve(`Image too large. Maximum: ${config.maxWidth}×${config.maxHeight}px`);
          } else {
            resolve(null);
          }
          URL.revokeObjectURL(img.src);
        };
        img.onerror = () => {
          resolve("Could not read image file");
          URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(file);
      });
    }
    
    return null;
  };
  
  const handleFileSelect = async (file) => {
    if (!file) return;
    
    setValidationError(null);
    
    // Client-side validation
    const error = await validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }
    
    // Create preview
    setPreviewUrl(URL.createObjectURL(file));
    
    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/admin/product-skin/upload/${assetType}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        onUploadSuccess(data);
        toast.success(`${config.label} uploaded successfully`);
      } else {
        const err = await res.json();
        setValidationError(err.detail || 'Upload failed');
        setPreviewUrl(null);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setValidationError('Upload failed. Please try again.');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };
  
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/product-skin/asset/${assetType}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        onDelete();
        setPreviewUrl(null);
        toast.success(`${config.label} removed`);
      } else {
        toast.error('Failed to delete asset');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete asset');
    } finally {
      setDeleting(false);
    }
  };
  
  const displayUrl = previewUrl || (currentUrl ? `${API_URL}${currentUrl}` : null);
  
  return (
    <div className="space-y-2">
      <Label>{config.label}</Label>
      
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-4 transition-colors
          ${dragOver ? 'border-purple-500 bg-purple-500/10' : 'border-white/10 hover:border-white/20'}
          ${validationError ? 'border-red-500/50' : ''}
        `}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid={`${assetType}-upload-zone`}
      >
        {displayUrl ? (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-black/40 flex items-center justify-center overflow-hidden">
              <img 
                src={displayUrl} 
                alt={config.label}
                className="max-w-full max-h-full object-contain"
                data-testid={`${assetType}-preview`}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-white font-medium flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                {config.label} uploaded
              </p>
              <p className="text-xs text-[#8A93A6]">{config.description}</p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-white/10"
                data-testid={`${assetType}-replace-btn`}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Replace'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid={`${assetType}-delete-btn`}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <div 
            className="text-center cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
                <p className="text-sm text-[#8A93A6]">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto text-[#8A93A6] mb-2" />
                <p className="text-sm text-white mb-1">
                  Drop file here or <span className="text-purple-400">browse</span>
                </p>
                <p className="text-xs text-[#8A93A6]">{config.constraints}</p>
              </>
            )}
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept={config.accept}
          onChange={(e) => handleFileSelect(e.target.files[0])}
          className="hidden"
          data-testid={`${assetType}-file-input`}
        />
      </div>
      
      {validationError && (
        <p className="text-xs text-red-400 flex items-center gap-1" data-testid={`${assetType}-error`}>
          <X className="h-3 w-3" />
          {validationError}
        </p>
      )}
      
      <p className="text-xs text-[#8A93A6]">{config.description}</p>
    </div>
  );
}

/**
 * Product Skin Page
 * 
 * Purpose: Single source of truth for platform UI branding
 * Location: Admin → App Settings → Product Skin
 * 
 * Controls:
 * - App logo & favicon
 * - App name (display name)
 * - Primary & secondary UI colors
 * - Navigation / header styling
 * - Footer & legal branding
 * - System emails
 * 
 * Does NOT control:
 * - Self-Marketing Engine content
 * - App Profiles
 * - Creator Profiles
 * - Brand Kits
 * - Social posts or marketing content
 * 
 * Safety Rules:
 * - Exactly one Product Skin exists
 * - Cannot be duplicated
 * - Cannot be deleted
 * - Cannot be selected by SME
 * - Cannot be overridden by App Profiles
 */
export default function ProductSkin() {
  const [skin, setSkin] = useState({
    app_name: 'Consistency',
    app_logo_url: '',
    symbol_url: '',
    favicon_url: '',
    primary_color: '#3A7BFF',
    secondary_color: '#39E6C8',
    header_bg_color: '#0F1117',
    footer_text: '© 2026 Consistency. All rights reserved.',
    system_email_from_name: 'Consistency',
    system_email_accent_color: '#3A7BFF'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSkin, setOriginalSkin] = useState(null);

  const fetchProductSkin = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/product-skin`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSkin(data);
        setOriginalSkin(data);
      }
    } catch (err) {
      console.error('Failed to fetch product skin:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProductSkin();
  }, [fetchProductSkin]);

  useEffect(() => {
    if (originalSkin) {
      // Compare only non-asset fields for text changes
      const compareFields = ['app_name', 'primary_color', 'secondary_color', 'header_bg_color', 'footer_text', 'system_email_from_name', 'system_email_accent_color'];
      const changed = compareFields.some(f => skin[f] !== originalSkin[f]);
      setHasChanges(changed);
    }
  }, [skin, originalSkin]);

  const handleChange = (field, value) => {
    setSkin(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/product-skin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          app_name: skin.app_name,
          primary_color: skin.primary_color,
          secondary_color: skin.secondary_color,
          header_bg_color: skin.header_bg_color,
          footer_text: skin.footer_text,
          system_email_from_name: skin.system_email_from_name,
          system_email_accent_color: skin.system_email_accent_color
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSkin(data);
        setOriginalSkin(data);
        setHasChanges(false);
        toast.success('Product Skin saved successfully');
      } else {
        toast.error('Failed to save Product Skin');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save Product Skin');
    } finally {
      setSaving(false);
    }
  };

  const handleAssetUpload = (assetType, data) => {
    // Update skin with new URL
    const urlField = assetType === 'logo' ? 'app_logo_url' : `${assetType}_url`;
    setSkin(prev => ({ ...prev, [urlField]: data.url }));
    setOriginalSkin(prev => ({ ...prev, [urlField]: data.url }));
  };

  const handleAssetDelete = (assetType) => {
    const urlField = assetType === 'logo' ? 'app_logo_url' : `${assetType}_url`;
    setSkin(prev => ({ ...prev, [urlField]: '' }));
    setOriginalSkin(prev => ({ ...prev, [urlField]: '' }));
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-800 rounded w-1/3"></div>
          <div className="h-64 bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="product-skin-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Palette className="h-8 w-8 text-purple-500" />
          <h1 className="text-3xl font-bold text-white">Product Skin</h1>
          <Badge variant="outline" className="border-purple-500/30 text-purple-400">
            <Lock className="h-3 w-3 mr-1" />
            Singleton
          </Badge>
        </div>
        <p className="text-[#8A93A6]">
          Platform UI branding — affects all Consistency-owned surfaces globally
        </p>
      </div>

      {/* Safety Notice */}
      <Alert className="mb-6 bg-purple-500/10 border-purple-500/30">
        <Shield className="h-4 w-4 text-purple-500" />
        <AlertDescription className="text-purple-300">
          <strong>Scope:</strong> Product Skin controls how Consistency looks as a product. 
          It does NOT affect marketing content, App Profiles, or Self-Marketing Engine output.
        </AlertDescription>
      </Alert>

      {/* App Identity */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5 text-cyan-500" />
            App Identity
          </CardTitle>
          <CardDescription>Core branding elements used across the platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>App Name</Label>
              <Input
                value={skin.app_name}
                onChange={(e) => handleChange('app_name', e.target.value)}
                placeholder="Consistency"
                className="bg-[#1C1F26] border-white/10 text-white mt-2"
                data-testid="app-name-input"
              />
              <p className="text-xs text-[#8A93A6] mt-1">Used in footer and system emails (not shown in header)</p>
            </div>
            <div>
              <Label>Footer Text</Label>
              <Input
                value={skin.footer_text}
                onChange={(e) => handleChange('footer_text', e.target.value)}
                placeholder="© 2026 Consistency. All rights reserved."
                className="bg-[#1C1F26] border-white/10 text-white mt-2"
                data-testid="footer-text-input"
              />
              <p className="text-xs text-[#8A93A6] mt-1">Legal footer across all pages</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Visual Assets */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5 text-green-500" />
            Visual Assets
          </CardTitle>
          <CardDescription>Logo, symbol, and favicon — upload only (PNG/SVG, no JPG)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <AssetUploader
              assetType="logo"
              currentUrl={skin.app_logo_url}
              onUploadSuccess={(data) => handleAssetUpload('logo', data)}
              onDelete={() => handleAssetDelete('logo')}
            />
            <AssetUploader
              assetType="symbol"
              currentUrl={skin.symbol_url}
              onUploadSuccess={(data) => handleAssetUpload('symbol', data)}
              onDelete={() => handleAssetDelete('symbol')}
            />
            <AssetUploader
              assetType="favicon"
              currentUrl={skin.favicon_url}
              onUploadSuccess={(data) => handleAssetUpload('favicon', data)}
              onDelete={() => handleAssetDelete('favicon')}
            />
          </div>
        </CardContent>
      </Card>

      {/* UI Colors */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-pink-500" />
            UI Colors
          </CardTitle>
          <CardDescription>Platform-wide color scheme for navigation, buttons, and accents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="color"
                  value={skin.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="h-10 w-16 p-1 bg-transparent border-white/10"
                  data-testid="primary-color-input"
                />
                <Input
                  value={skin.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="bg-[#1C1F26] border-white/10 text-white flex-1"
                />
              </div>
              <p className="text-xs text-[#8A93A6] mt-1">Buttons, links, active states</p>
            </div>
            <div>
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="color"
                  value={skin.secondary_color}
                  onChange={(e) => handleChange('secondary_color', e.target.value)}
                  className="h-10 w-16 p-1 bg-transparent border-white/10"
                  data-testid="secondary-color-input"
                />
                <Input
                  value={skin.secondary_color}
                  onChange={(e) => handleChange('secondary_color', e.target.value)}
                  className="bg-[#1C1F26] border-white/10 text-white flex-1"
                />
              </div>
              <p className="text-xs text-[#8A93A6] mt-1">Accents, highlights</p>
            </div>
            <div>
              <Label>Header Background</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="color"
                  value={skin.header_bg_color}
                  onChange={(e) => handleChange('header_bg_color', e.target.value)}
                  className="h-10 w-16 p-1 bg-transparent border-white/10"
                  data-testid="header-bg-color-input"
                />
                <Input
                  value={skin.header_bg_color}
                  onChange={(e) => handleChange('header_bg_color', e.target.value)}
                  className="bg-[#1C1F26] border-white/10 text-white flex-1"
                />
              </div>
              <p className="text-xs text-[#8A93A6] mt-1">Navigation bar background</p>
            </div>
          </div>

          {/* Color Preview */}
          <div className="mt-4 p-4 rounded-lg bg-black/40">
            <Label className="text-[#8A93A6] text-sm mb-2 block">Preview</Label>
            <div 
              className="rounded-lg overflow-hidden"
              style={{ backgroundColor: skin.header_bg_color }}
            >
              <div className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {skin.app_logo_url ? (
                    <img 
                      src={`${API_URL}${skin.app_logo_url}`} 
                      alt="Logo" 
                      className="h-8 w-auto object-contain"
                    />
                  ) : (
                    <span className="text-white font-semibold">[Logo]</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <span 
                    className="px-3 py-1 rounded text-white text-sm"
                    style={{ backgroundColor: skin.primary_color }}
                  >
                    Primary
                  </span>
                  <span 
                    className="px-3 py-1 rounded text-white text-sm"
                    style={{ backgroundColor: skin.secondary_color }}
                  >
                    Secondary
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Emails */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-500" />
            System Emails
          </CardTitle>
          <CardDescription>Branding for password reset, confirmations, and notices</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>From Name</Label>
              <Input
                value={skin.system_email_from_name}
                onChange={(e) => handleChange('system_email_from_name', e.target.value)}
                placeholder="Consistency"
                className="bg-[#1C1F26] border-white/10 text-white mt-2"
                data-testid="email-from-name-input"
              />
              <p className="text-xs text-[#8A93A6] mt-1">Sender name for system emails</p>
            </div>
            <div>
              <Label>Email Accent Color</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="color"
                  value={skin.system_email_accent_color}
                  onChange={(e) => handleChange('system_email_accent_color', e.target.value)}
                  className="h-10 w-16 p-1 bg-transparent border-white/10"
                  data-testid="email-accent-color-input"
                />
                <Input
                  value={skin.system_email_accent_color}
                  onChange={(e) => handleChange('system_email_accent_color', e.target.value)}
                  className="bg-[#1C1F26] border-white/10 text-white flex-1"
                />
              </div>
              <p className="text-xs text-[#8A93A6] mt-1">Buttons and links in emails</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scope Clarification */}
      <Card className="glass mb-6 bg-blue-500/5 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white mb-2">What Product Skin Does NOT Control</p>
              <ul className="text-sm text-[#8A93A6] space-y-1">
                <li>• Self-Marketing Engine content or landing pages</li>
                <li>• App Profiles (marketing identities for SME)</li>
                <li>• Creator Profiles or Brand Kits</li>
                <li>• Social posts or generated content</li>
              </ul>
              <p className="text-sm text-blue-400 mt-3">
                <strong>To market Consistency itself:</strong> Create a "Consistency" App Profile with its own Brand Kits.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`${hasChanges ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600'} px-6`}
          data-testid="save-product-skin-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Product Skin'}
        </Button>
      </div>
    </div>
  );
}
